/**
 * DepartmentsTab - Elegant Professional Design with Head Photos
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Plus, ChevronRight } from 'lucide-react';

const translations = {
  en: {
    departments: 'Departments',
    subtitle: 'Practice areas and specializations',
    create: 'Add Department',
    teams: 'teams',
    lawyers: 'lawyers'
  },
  no: {
    departments: 'Avdelinger',
    subtitle: 'Fagområder og spesialiseringer',
    create: 'Legg til avdeling',
    teams: 'team',
    lawyers: 'advokater'
  }
};

const mockDepartments = [
  { id: 'd1', name: 'Corporate & M&A', lawyers: 14, teams: 3, head: 'Sarah Chen', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=32' },
  { id: 'd2', name: 'Litigation', lawyers: 11, teams: 2, head: 'Michael Berg', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=33' },
  { id: 'd3', name: 'Employment', lawyers: 8, teams: 2, head: 'Anna Lindqvist', title: 'Senior Partner', headImg: 'https://i.pravatar.cc/150?img=25' },
  { id: 'd4', name: 'Real Estate', lawyers: 6, teams: 1, head: 'Erik Johansen', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=12' },
  { id: 'd5', name: 'Tax', lawyers: 5, teams: 1, head: 'Lisa Andersen', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=44' },
  { id: 'd6', name: 'IP & Technology', lawyers: 7, teams: 2, head: 'David Kim', title: 'Senior Partner', headImg: 'https://i.pravatar.cc/150?img=52' },
  { id: 'd7', name: 'Banking & Finance', lawyers: 9, teams: 2, head: 'Marie Hansen', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=23' },
  { id: 'd8', name: 'Competition', lawyers: 4, teams: 1, head: 'Thomas Eriksen', title: 'Partner', headImg: 'https://i.pravatar.cc/150?img=59' },
];

interface DepartmentsTabProps {
  departments: any[];
  users: any[];
  onCreate: () => void;
  onRefresh: () => void;
  language?: string;
}

const DepartmentsTab: React.FC<DepartmentsTabProps> = ({ onCreate, language = 'en' }) => {
  const t = translations[language] || translations.en;
  const totalLawyers = mockDepartments.reduce((a, d) => a + d.lawyers, 0);
  const totalTeams = mockDepartments.reduce((a, d) => a + d.teams, 0);

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-10">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t.departments}</h1>
          <p className="text-gray-400 text-sm mt-1">{mockDepartments.length} departments · {totalLawyers} {t.lawyers} · {totalTeams} {t.teams}</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onCreate}
          className="h-9 px-4 bg-gray-900 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t.create}
        </motion.button>
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-2 gap-4">
        {mockDepartments.map((dept, index) => (
          <motion.div
            key={dept.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className="group bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-5 cursor-pointer transition-all hover:shadow-md"
          >
            {/* Top row */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 text-[15px]">{dept.name}</h3>
                <p className="text-sm text-gray-400 mt-0.5">{dept.lawyers} {t.lawyers} · {dept.teams} {t.teams}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-200 group-hover:text-gray-400 transition-colors mt-0.5" strokeWidth={1.5} />
            </div>

            {/* Department Head */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
              <img 
                src={dept.headImg}
                alt={dept.head}
                className="w-9 h-9 rounded-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{dept.head}</p>
                <p className="text-xs text-gray-400">{dept.title}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default DepartmentsTab;
